import React from 'react';
import { fg } from '@atlaskit/platform-feature-flags';
import { type OptionData } from '@atlaskit/user-picker';
import { type OptionProps as UserPickerOptionProps } from '@atlaskit/user-picker/option';
import { type OptionProps as SelectOptionProps } from '@atlaskit/select';
import { useInviteeListStateContext } from './InviteeListContext';

// Type alias for @atlaskit/select's expected Option component type
type SelectOptionComponent = React.ComponentType<SelectOptionProps<OptionData, boolean>>;

/**
 * Safely extracts the actual option data from react-select's wrapped format.
 * react-select wraps OptionData in { label, value, data: OptionData }
 * but sometimes passes OptionData directly.
 *
 * @param optionData The option data (may be wrapped or unwrapped)
 * @returns The unwrapped OptionData
 */
const extractOptionData = (optionData: OptionData | { data: OptionData }): OptionData => {
	return 'data' in optionData ? optionData.data : optionData;
};

/**
 * Determines if ProfileCard rendering should be attempted for a given option.
 * This filters by option type to exclude options that should never have a ProfileCard:
 * - 'email' - Email addresses don't have ProfileCards
 * - 'group' - User groups don't have user ProfileCards
 * - 'custom' - Synthetic options like "Unassigned", "Automatic", etc.
 *
 * The final decision of whether to show a ProfileCard is delegated to the consumer
 * via the renderOptionWithProfileCard callback, which receives the full optionData.
 * The consumer is responsible for:
 * - Validating the id is not synthetic (e.g., '-1', '__UNASSIGNED_ID__', etc.)
 * - Deciding which ProfileCard component to render (User, Agent, Team, etc.)
 *
 * @param optionData The data for the option (wrapped by react-select).
 * @returns True if the option type could potentially show a ProfileCard.
 */
const shouldShowProfileCardForOption = (optionData: OptionData): boolean => {
	const actualData = extractOptionData(optionData);
	const type = actualData.type;

	// Only attempt ProfileCard for user, external_user, and team types
	// This automatically filters out:
	// - type: 'email' (email addresses)
	// - type: 'group' (user groups)
	// - type: 'custom' (synthetic options like __UNASSIGNED_ID__)
	// - type: undefined (malformed data)
	return type === 'user' || type === 'external_user' || type === 'team';
};

/**
 * Factory function to create a custom Option component that can wrap children with a ProfileCard.
 * This component is conditionally rendered based on a feature flag and the presence of a `renderOptionWithProfileCard` prop.
 *
 * @param StandardOption The standard Option component to be wrapped.
 * @returns A new Option component that conditionally renders a ProfileCard.
 */
export const createOptionWithProfileCard = (
	StandardOption: React.ComponentType<UserPickerOptionProps>,
): SelectOptionComponent => {
	const OptionWithProfileCard = (props: SelectOptionProps<OptionData, boolean>) => {
		const { data: optionData } = props;
		const { renderOptionWithProfileCard } = useInviteeListStateContext();

		// Cast props to work with user-picker's Option component
		// This is safe because react-select passes a superset of the required props
		const userPickerProps = props as unknown as UserPickerOptionProps;

		// Platform feature gate: If disabled, render the standard option directly.
		if (!fg('platform_jira_ai_invite_people_with_profilecard')) {
			return <StandardOption {...userPickerProps} />;
		}

		// If no custom renderer is provided, or the option type doesn't support a ProfileCard,
		// render the standard option.
		if (!renderOptionWithProfileCard || !shouldShowProfileCardForOption(optionData)) {
			return <StandardOption {...userPickerProps} />;
		}

		// Render the option wrapped by the consumer's ProfileCard renderer
		return (
			<>{renderOptionWithProfileCard(props, optionData, <StandardOption {...userPickerProps} />)}</>
		);
	};

	return OptionWithProfileCard;
};
